// src/pages/VoicePackagesPage.js
//
// E-Sim Paketleri (Ses) — "E-Sim Paketleri (Sınırsız)" sayfasının ses paketleri
// için eşi. Görünüm ortak kalsın diye AYNI CSS modülünü kullanır
// (ESimPackagesPage.module.css).
//
// Veri kaynağı sınırsız sayfasıyla aynı: /v2/admin/bundles → bundle > plan.
// Tek fark aile filtresi (family=voice) ve plan satırında konuşma/SMS hakkının
// gösterilmesi — bu paketleri ses paketi yapan şey o.
//
// v2'nin iki kuralı korunur:
//   1) PATCH'te yalnızca DEĞİŞEN alan gönderilir — gönderilmeyen alana dokunulmaz.
//      (Fazladan alan göndermek gereksiz bir Google Play fiyat push'u tetikler.)
//   2) Taban fiyatı olmayan planın Play anahtarı değiştirilemez (PRICE_REQUIRED).

import React, { useState, useEffect, useCallback } from 'react';
import { toast } from 'react-toastify';
import styles from './ESimPackagesPage.module.css';
import LoadingSpinner from '../components/common/LoadingSpinner';
import StatusToggle from '../components/common/StatusToggle';
import BundleControls from '../components/catalog/BundleControls';
import {
    fetchAdminBundles,
    updateBundlePlan,
    buildPlanPatchFromEdit,
    readApiError,
    formatMoney,
    formatDataAmount,
    formatVoiceSms,
    scopeLabel,
    sortPlans,
    SCOPES,
} from '../services/catalogApi';

const isValidPrice = (value) => {
    const n = parseFloat(value);
    return Number.isFinite(n) && n > 0;
};

// Fiyat alanı "199.90" gibi string gelir; input için string tutulur.
const asInputValue = (value) => (value === null || value === undefined ? '' : String(value));

const planToEditState = (plan) => ({
    basePriceTry: asInputValue(plan.basePriceTry),
    isOpenGp: !!plan.isOpenGp,
    isOpenAp: !!plan.isOpenAp,
    isOpenWeb: !!plan.isOpenWeb,
});

const countryLabel = (country) => country.nameSelected || country.name;

const VoicePackagesPage = () => {
    const [allBundles, setAllBundles] = useState([]);
    const [filteredBundles, setFilteredBundles] = useState([]);
    const [countriesForFilter, setCountriesForFilter] = useState([]);

    const [scopesForFilter, setScopesForFilter] = useState([]);

    const [selectedCountryName, setSelectedCountryName] = useState('all');
    const [selectedScope, setSelectedScope] = useState('all');
    const [searchTerm, setSearchTerm] = useState('');
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState(null);
    // Varsayılan: yalnızca aktifler. Fiyatı girilmiş ama henüz açılmamış paketi
    // bulup aktif etmek için açılır.
    const [showInactive, setShowInactive] = useState(false);

    const [editingStates, setEditingStates] = useState({}); // { planId: {…} }
    const [savingStates, setSavingStates] = useState({});   // { planId: bool }

    // --- API'den Veri Çekme ---
    const fetchPackagesFromApi = useCallback(async () => {
        setIsLoading(true);
        setError(null);
        try {
            const data = await fetchAdminBundles({
                family: 'voice',
                includeInactive: showInactive ? undefined : false,
            });
            setAllBundles(data);

            // Ülke filtresi: bundle'ların kapsadığı ülkelerin birleşimi.
            const seen = new Set();
            const uniqueCountries = [];
            data.forEach((bundle) => {
                (bundle.countries || []).forEach((country) => {
                    const label = countryLabel(country);
                    if (label && !seen.has(label)) {
                        seen.add(label);
                        uniqueCountries.push({ name: label });
                    }
                });
            });
            setCountriesForFilter(uniqueCountries.sort((a, b) => a.name.localeCompare(b.name, 'tr')));

            // Kapsam filtresi: bölgesel ses paketi (Hercules) 37 tek ülke paketinin
            // arasında kaybolmasın. Başlığa göre değil scope'a göre ayrılır —
            // ikinci bir bölgesel paket gelirse kendiliğinden doğru yere düşer.
            const presentScopes = new Set(data.map((bundle) => bundle.scope).filter(Boolean));
            setScopesForFilter(SCOPES.filter((s) => presentScopes.has(s.value)));

            // Düzenleme state'ini sunucudan gelen değerlerle doldur.
            const initialEdits = {};
            data.forEach((bundle) => {
                (bundle.plans || []).forEach((plan) => {
                    initialEdits[String(plan.id)] = planToEditState(plan);
                });
            });
            setEditingStates(initialEdits);
        } catch (err) {
            console.error('Ses paketi yükleme hatası:', err);
            // Liste ucunda 404 "kayıt yok" demek değil: eşleşme olmadığında sunucu
            // 200 + boş dizi döner. 404 = /v2 uçları bu sunucuda yayında değil.
            if (err?.response?.status === 404) {
                setError('Katalog v2 uçları bu sunucuda henüz yayında değil (404).');
            } else {
                setError(readApiError(err, 'Paketler yüklenirken bir hata oluştu.').message);
            }
            setAllBundles([]);
        } finally {
            setIsLoading(false);
        }
    }, [showInactive]);

    useEffect(() => {
        fetchPackagesFromApi();
    }, [fetchPackagesFromApi]);

    // --- Filtrelenmiş Paketleri Hesaplama ---
    useEffect(() => {
        if (!allBundles.length) {
            setFilteredBundles([]);
            return;
        }

        let filteredData = allBundles;

        // 1. Ülkeye göre filtrele
        if (selectedCountryName !== 'all') {
            filteredData = filteredData.filter((bundle) =>
                (bundle.countries || []).some((c) => countryLabel(c) === selectedCountryName)
            );
        }

        // 2. Kapsama göre filtrele (ülke paketi / bölgesel)
        if (selectedScope !== 'all') {
            filteredData = filteredData.filter((bundle) => bundle.scope === selectedScope);
        }

        // 3. Arama terimine göre filtrele (başlık, açıklama veya plan adı)
        if (searchTerm.trim() !== '') {
            const lowerSearchTerm = searchTerm.toLowerCase();
            filteredData = filteredData.filter((bundle) => {
                const bundleMatch =
                    (bundle.title || '').toLowerCase().includes(lowerSearchTerm) ||
                    (bundle.description || '').toLowerCase().includes(lowerSearchTerm);

                const planMatch = (bundle.plans || []).some((plan) =>
                    (plan.title || '').toLowerCase().includes(lowerSearchTerm)
                );

                return bundleMatch || planMatch;
            });
        }

        setFilteredBundles(filteredData);
    }, [allBundles, selectedCountryName, selectedScope, searchTerm]);

    const handleCountryFilterChange = (event) => setSelectedCountryName(event.target.value);
    const handleScopeFilterChange = (event) => setSelectedScope(event.target.value);
    const handleSearchChange = (event) => setSearchTerm(event.target.value);

    const handleInputChange = (planId, field, value) => {
        setEditingStates((prev) => ({
            ...prev,
            [planId]: { ...prev[planId], [field]: value },
        }));
    };

    const handleCheckboxChange = (planId, field, checked) => {
        setEditingStates((prev) => ({
            ...prev,
            [planId]: { ...prev[planId], [field]: checked },
        }));
    };

    // Sunucudan dönen güncel planı listeye işler: fiyat kaydından sonra Google'ın
    // vergi dahil referans fiyatları ve priceId yenilenir, ekran da yenilenmeli.
    const mergePlanIntoState = (planId, nextPlan) => {
        setAllBundles((prev) =>
            prev.map((bundle) => ({
                ...bundle,
                plans: (bundle.plans || []).map((plan) =>
                    String(plan.id) === planId ? { ...plan, ...nextPlan } : plan
                ),
            }))
        );
    };

    // Yayın kontrollerinden (aktif/pasif, Play) dönen alanları bundle'a işler.
    const mergeBundleIntoState = (bundleId, fields) => {
        setAllBundles((prev) => prev.map((bundle) => (bundle.id === bundleId ? { ...bundle, ...fields } : bundle)));
    };

    const handleSaveChanges = async (plan) => {
        const planId = String(plan.id);
        const editedState = editingStates[planId];
        if (!editedState) return;

        // Yalnızca değişen alanlar gönderilir.
        const patch = buildPlanPatchFromEdit(editedState, planToEditState(plan));

        if (Object.keys(patch).length === 0) {
            toast.info('Değişiklik yok.');
            return;
        }
        if ('basePriceTry' in patch && !isValidPrice(patch.basePriceTry)) {
            toast.error('Lütfen geçerli bir fiyat girin.');
            return;
        }

        setSavingStates((prev) => ({ ...prev, [planId]: true }));

        try {
            const updated = await updateBundlePlan(plan.id, patch);
            // Sunucu güncel planı döndürüyorsa onu kullan; döndürmüyorsa en azından
            // gönderdiğimiz alanları yansıt.
            mergePlanIntoState(planId, updated && updated.id ? updated : patch);
            setEditingStates((prev) => ({
                ...prev,
                [planId]: planToEditState({ ...plan, ...(updated && updated.id ? updated : patch) }),
            }));
            toast.success(`Paket (ID: ${planId}) başarıyla güncellendi.`);
        } catch (err) {
            console.error(`Plan ${planId} güncellenirken API hatası:`, err);
            const { code, message } = readApiError(err, 'Paket güncellenirken bir hata oluştu.');
            toast.error(message);
            // STORE_PUSH_FAILED'de sunucuda hiçbir alan yazılmadı; kullanıcının girdisi
            // formda kalsın ki tekrar deneyebilsin. Diğer hatalarda forma sunucudaki
            // değerler geri yüklenir.
            if (code !== 'STORE_PUSH_FAILED') {
                setEditingStates((prev) => ({ ...prev, [planId]: planToEditState(plan) }));
            }
        } finally {
            setSavingStates((prev) => ({ ...prev, [planId]: false }));
        }
    };

    // Kart başlığındaki etiket: tek ülkeliyse ülke adı, değilse kapsam + ülke sayısı.
    const coverageTag = (bundle) => {
        const countries = bundle.countries || [];
        if (countries.length === 1) return countryLabel(countries[0]);
        if (countries.length > 1) return `${scopeLabel(bundle.scope)} · ${countries.length} ülke`;
        return scopeLabel(bundle.scope);
    };

    return (
        <div className={styles.container}>
            <div className={styles.header}>
                <div className={styles.headerText}>
                    <h1 className={styles.title}>E-SIM Paketleri (Ses)</h1>
                    <p className={styles.subtitle}>
                        Konuşma ve SMS içeren paketlerin fiyatlarını ve mağaza durumlarını yönetin.
                    </p>
                </div>
                <div className={styles.infoNotes}>
                    <div className={styles.infoNote}>
                        <span className={styles.infoIcon}>ℹ️</span>
                        <span className={styles.infoText}>Girilen fiyatlar vergi hariçtir.</span>
                    </div>
                    <div className={styles.infoNote}>
                        <span className={styles.infoIcon}>ℹ️</span>
                        <span className={styles.infoText}>
                            Fiyat veya Google Play anahtarı kaydedildiğinde fiyat Play'e gönderilir.
                        </span>
                    </div>
                </div>
            </div>

            <div className={styles.filters}>
                <div className={styles.filterGroup}>
                    <label htmlFor="countryFilter">Ülkeye Göre Filtrele:</label>
                    <select id="countryFilter" value={selectedCountryName} onChange={handleCountryFilterChange} className={styles.selectInput}>
                        <option value="all">Tüm Ülkeler</option>
                        {countriesForFilter.map(country => (
                            <option key={country.name} value={country.name}>
                                {country.name}
                            </option>
                        ))}
                    </select>
                </div>
                {scopesForFilter.length > 1 && (
                    <div className={styles.filterGroup}>
                        <label htmlFor="scopeFilter">Kapsama Göre Filtrele:</label>
                        <select id="scopeFilter" value={selectedScope} onChange={handleScopeFilterChange} className={styles.selectInput}>
                            <option value="all">Tüm Kapsamlar</option>
                            {scopesForFilter.map(scope => (
                                <option key={scope.value} value={scope.value}>
                                    {scope.label}
                                </option>
                            ))}
                        </select>
                    </div>
                )}
                <div className={styles.filterGroup}>
                    <label htmlFor="searchFilter">Paket veya Ürün Ara:</label>
                    <input
                        type="text"
                        id="searchFilter"
                        placeholder="Austria, 20 GB, vs..."
                        value={searchTerm}
                        onChange={handleSearchChange}
                        className={styles.searchInput}
                    />
                </div>
                <div className={styles.filterToggle}>
                    <StatusToggle
                        label="Pasif paketleri göster"
                        checked={showInactive}
                        disabled={isLoading}
                        onChange={(e) => setShowInactive(e.target.checked)}
                        onText="Pasifler gösteriliyor"
                        offText="Pasifleri göster"
                    />
                </div>
            </div>

            <div className={styles.contentArea}>
                {isLoading ? (
                    <div className={styles.loadingContainer}><LoadingSpinner /></div>
                ) : error ? (
                    <div className={styles.errorPanel}>
                        <span className={styles.errorIcon}>⚠️</span>
                        <p className={styles.errorMessage}>{error}</p>
                        <button className={`${styles.retryButton} ghost`} onClick={() => fetchPackagesFromApi()}>Tekrar dene</button>
                    </div>
                ) : filteredBundles.length === 0 ? (
                    <p className={styles.emptyState}>
                        Filtrelerinize uygun {showInactive ? '' : 'aktif '}paket bulunamadı.
                        {!showInactive && ' Henüz yayına alınmamış bir paketi arıyorsanız “Pasifleri göster”i açın.'}
                    </p>
                ) : (
                    filteredBundles.map(bundle => (
                        <div key={bundle.id} className={styles.productCard}>
                            <div className={styles.productHeader}>
                                {bundle.imgUrl && (
                                    <img src={bundle.imgUrl} alt={bundle.title} className={styles.productImage} />
                                )}
                                <div className={styles.productInfo}>
                                    <h2 className={styles.productName}>
                                        {bundle.title} <span className={styles.countryTag}>{coverageTag(bundle)}</span>
                                    </h2>
                                    <p className={styles.productDescription}>{bundle.description}</p>
                                </div>
                                <BundleControls bundle={bundle} onUpdated={mergeBundleIntoState} />
                            </div>
                            <div className={styles.tableWrap}>
                                <table className={styles.refillsTable}>
                                    <thead>
                                        <tr>
                                            <th>Paket</th>
                                            <th className={styles.numCol}>Fiyat (TL)</th>
                                            <th className={styles.toggleCol}>Google Play</th>
                                            <th className={styles.toggleCol}>App Store</th>
                                            <th className={styles.toggleCol}>Web</th>
                                            <th className={styles.actionCol}>İşlemler</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {sortPlans(bundle.plans || []).map(plan => {
                                            const planId = String(plan.id);
                                            const currentEditState = editingStates[planId] || {};
                                            const isSavingThisRow = savingStates[planId] || false;
                                            // Taban fiyatı olmayan planın Play durumu değiştirilemez.
                                            const hasBase = isValidPrice(currentEditState.basePriceTry)
                                                || isValidPrice(plan.basePriceTry);
                                            // Plan başlığı İngilizce geliyor; içeriği Türkçe özetliyoruz.
                                            const voiceSms = formatVoiceSms(plan);

                                            return (
                                                <tr key={planId}>
                                                    <td>
                                                        <div className="num">{plan.title}</div>
                                                        <div className={styles.planMeta}>
                                                            <span className="num">{formatDataAmount(plan)}</span>
                                                            <span> · </span>
                                                            <span className="num">{plan.validityDays ?? '—'} gün</span>
                                                            {voiceSms && (
                                                                <>
                                                                    <span> · </span>
                                                                    <span>{voiceSms}</span>
                                                                </>
                                                            )}
                                                        </div>
                                                    </td>
                                                    <td className={styles.numCol}>
                                                        <input type="number" value={currentEditState.basePriceTry || ''} onChange={(e) => handleInputChange(planId, 'basePriceTry', e.target.value)} className={styles.priceInput} step="0.01" disabled={isSavingThisRow} />
                                                        <div className={styles.taxPrices} title="Vergi dahil satış fiyatları">
                                                            <span className={styles.taxLabel}>Vergi dahil</span>
                                                            <span className={`${styles.taxVals} num`}>₺{formatMoney(plan.priceTry)} · €{formatMoney(plan.priceEur)} · ${formatMoney(plan.priceUsd)}</span>
                                                        </div>
                                                    </td>
                                                    <td className={styles.toggleCell}>
                                                        <label className={styles.switch} title={hasBase ? undefined : 'Önce fiyat girip kaydedin'}>
                                                            <input type="checkbox" checked={currentEditState.isOpenGp || false} onChange={(e) => handleCheckboxChange(planId, 'isOpenGp', e.target.checked)} disabled={isSavingThisRow || !hasBase} />
                                                            <span className={`${styles.slider} ${styles.round}`}></span>
                                                        </label>
                                                    </td>
                                                    <td className={styles.toggleCell}>
                                                        <label className={styles.switch}>
                                                            <input type="checkbox" checked={currentEditState.isOpenAp || false} onChange={(e) => handleCheckboxChange(planId, 'isOpenAp', e.target.checked)} disabled={isSavingThisRow} />
                                                            <span className={`${styles.slider} ${styles.round}`}></span>
                                                        </label>
                                                    </td>
                                                    <td className={styles.toggleCell}>
                                                        <label className={styles.switch}>
                                                            <input type="checkbox" checked={currentEditState.isOpenWeb || false} onChange={(e) => handleCheckboxChange(planId, 'isOpenWeb', e.target.checked)} disabled={isSavingThisRow} />
                                                            <span className={`${styles.slider} ${styles.round}`}></span>
                                                        </label>
                                                    </td>
                                                    <td className={styles.actionCol}>
                                                        <button onClick={() => handleSaveChanges(plan)} className={styles.saveButton} disabled={isSavingThisRow}>
                                                            {isSavingThisRow ? 'Kaydediliyor...' : 'Kaydet'}
                                                        </button>
                                                    </td>
                                                </tr>
                                            );
                                        })}
                                    </tbody>
                                </table>
                            </div>
                        </div>
                    ))
                )}
            </div>
        </div>
    );
};

export default VoicePackagesPage;
